<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, ref, watch} from 'vue';
import {generateAriaId} from '../modules/fomantic/base.ts';
import {entryDetail, entryExplanation, sameNumber, type ResolveTarget} from '../utils/drcyCellNotes.ts';

// The panel that opens on a flagged sheet cell: it shows why the engine left
// the cell open, offers the candidates it knows and a free value, and lets
// the engineer either write the value into the sheet or hand the question to
// DRCY. It is teleported out of the virtualized row it came from, follows its
// badge while the sheet scrolls, and renders inside the modal workspace when
// the badge sits there, since assistive tech treats the rest of the page as
// inert while a modal dialog is open.
const props = defineProps<{
  target: ResolveTarget | null;
}>();

const emit = defineEmits<{
  save: [value: string];
  ask: [text: string];
  close: [];
}>();

const titleId = generateAriaId();
const groupName = generateAriaId();
const hintId = generateAriaId();
const panel = ref<HTMLElement | null>(null);
const valueInput = ref<HTMLInputElement | null>(null);
// The pick, by position: two candidates may state one number from different
// pages, and a candidate may be any text, so neither is a usable key.
const CUSTOM = -1;
const choice = ref(CUSTOM);
const custom = ref('');
const style = ref<Record<string, string>>({});

const options = computed(() => props.target?.entry.options ?? []);
const chosen = computed(() => (choice.value === CUSTOM ? null : options.value[choice.value] ?? null));
const stateWord = computed(() => props.target?.flag.replace('_', ' ') ?? '');
const reason = computed(() => (props.target ? entryDetail(props.target.entry) : ''));
// The unit is the engine's, from the sidecar; a candidate states its own.
const unit = computed(() => chosen.value?.unit ?? props.target?.unit ?? '');
const value = computed(() => chosen.value?.value ?? custom.value.trim());
// What the engine reads as a number in a cell; anything else a refresh would
// not keep.
const NUMBER = /^-?\d+(?:\.\d+)?(?:e[-+]?\d+)?$/i;
const isNumber = computed(() => NUMBER.test(value.value));
// The engine keeps a value only where it differs from what it wrote, so
// saving the cell's own number would change nothing.
const unchanged = computed(() => isNumber.value && sameNumber(value.value, props.target?.value ?? ''));
const canSave = computed(() => props.target?.editable === true && isNumber.value && !unchanged.value);
const hint = computed(() => {
  if (!props.target?.editable || !value.value) return '';
  if (!isNumber.value) return 'Enter a number, such as 1.8.';
  if (unchanged.value) return 'The sheet already holds this value; ask DRCY to confirm it instead.';
  return '';
});

const askText = computed(() => {
  const target = props.target;
  if (!target) return '';
  const cell = `Cell ${target.reference}, ${target.header}`;
  if (value.value) {
    const amount = `${value.value}${unit.value ? ` ${unit.value}` : ''}`;
    // A candidate's source is short ("p.5"); the cell's own note may carry a
    // parenthesised source of its own, so it follows as a sentence.
    if (chosen.value?.source) return `${cell}: use ${amount} (${chosen.value.source}) and refresh the report.`;
    const note = entryExplanation(target.entry);
    return `${cell}: use ${amount} and refresh the report.${note ? ` Its note: ${note}.` : ''}`;
  }
  return `${cell} is ${stateWord.value}${reason.value ? `: ${reason.value}` : ''}. How should we resolve it?`;
});

const container = computed(() => props.target?.anchor.closest<HTMLElement>('[aria-modal="true"]') ?? 'body');

const MARGIN = 8;
const GAP = 4;

// The layout viewport, without the scrollbar `innerWidth` counts.
function viewportSize(): {width: number; height: number} {
  const root = document.documentElement;
  return {width: root.clientWidth || window.innerWidth, height: root.clientHeight || window.innerHeight};
}

async function place(anchor: HTMLElement) {
  const rect = anchor.getBoundingClientRect();
  // Measured after render so the panel's own size is known; clamped to the
  // viewport because the badge may sit near an edge or on a narrow screen.
  style.value = {top: `${rect.bottom + GAP}px`, left: `${rect.left}px`};
  await nextTick();
  const box = panel.value?.getBoundingClientRect();
  if (!box) return;
  const {width, height} = viewportSize();
  const left = Math.max(MARGIN, Math.min(rect.left, width - box.width - MARGIN));
  let top = rect.bottom + GAP;
  if (top + box.height > height - MARGIN) top = rect.top - box.height - GAP;
  // A badge scrolled partly out of view still gets a panel inside the viewport.
  top = Math.max(MARGIN, Math.min(top, height - box.height - MARGIN));
  style.value = {top: `${top}px`, left: `${left}px`};
}

// Where focus goes back when the panel closes: the badge, or the sheet when
// the badge left with the edit it asked for (a resolved cell has no badge).
let opener: {anchor: HTMLElement; viewport: HTMLElement | null} | null = null;

function restoreFocus() {
  const active = document.activeElement;
  // Focus the engineer moved on purpose (the composer after "Ask DRCY") stays.
  if (active && active !== document.body && active.isConnected) return;
  const back = opener?.anchor.isConnected ? opener.anchor : opener?.viewport?.isConnected ? opener.viewport : null;
  back?.focus({preventScroll: true});
}

watch(() => props.target, async (target) => {
  if (!target) {
    const hadFocus = panel.value?.contains(document.activeElement) === true;
    await nextTick();
    if (hadFocus) restoreFocus();
    opener = null;
    return;
  }
  opener = {anchor: target.anchor, viewport: target.anchor.closest<HTMLElement>('.drcy-csv-viewport')};
  // A number already in the cell is the engineer's starting point; a gap
  // word is not.
  const current = target.value.trim();
  choice.value = target.entry.options?.length ? 0 : CUSTOM;
  custom.value = NUMBER.test(current) ? current : '';
  await place(target.anchor);
  (panel.value?.querySelector<HTMLElement>('input[type="radio"]:checked') ?? valueInput.value ?? panel.value?.querySelector<HTMLElement>('button'))?.focus();
}, {immediate: true});

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    // The workspace closes on Escape at the window level; this one is ours.
    event.preventDefault();
    event.stopPropagation();
    emit('close');
  } else if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.type === 'text') {
    event.preventDefault();
    event.stopPropagation();
    if (canSave.value) emit('save', value.value);
  }
}

// Clicks inside the panel stop at its root, so any click that reaches the
// document is outside it, even one on a control that removed itself.
function onDocumentClick(event: MouseEvent) {
  if (!props.target) return;
  if (panel.value && event.composedPath().includes(panel.value)) return;
  emit('close');
}

// Keep the panel on its badge as the sheet, the chat or the page scroll, and
// close it once the badge is gone or scrolled out of sight.
function follow(scrolled: EventTarget | null) {
  const target = props.target;
  if (!target) return;
  const anchor = target.anchor;
  if (!anchor.isConnected) {
    emit('close');
    return;
  }
  const rect = anchor.getBoundingClientRect();
  const {width, height} = viewportSize();
  const bounds = scrolled instanceof Element ? scrolled.getBoundingClientRect() : {top: 0, left: 0, bottom: height, right: width};
  const visible = rect.bottom > Math.max(bounds.top, 0) && rect.top < Math.min(bounds.bottom, height) &&
    rect.right > Math.max(bounds.left, 0) && rect.left < Math.min(bounds.right, width);
  if (!visible) {
    emit('close');
    return;
  }
  place(anchor);
}

let frame = 0;
function schedule(scrolled: EventTarget | null) {
  if (frame) cancelAnimationFrame(frame);
  frame = requestAnimationFrame(() => {
    frame = 0;
    follow(scrolled);
  });
}

function onScroll(event: Event) {
  const target = props.target;
  if (!target) return;
  const scrolled = event.target;
  // The panel's own field or list scrolling moves nothing it points at, and
  // neither does a scroll container the badge is not in.
  if (scrolled instanceof Node && panel.value?.contains(scrolled)) return;
  if (scrolled instanceof Node && scrolled !== document && !scrolled.contains(target.anchor)) return;
  schedule(scrolled);
}

function onResize() {
  if (props.target) schedule(document);
}

document.addEventListener('click', onDocumentClick);
// Scroll does not bubble, so it is captured; the panel is not a sticky
// element an observer could track.
// eslint-disable-next-line github/prefer-observers
document.addEventListener('scroll', onScroll, {capture: true});
// eslint-disable-next-line github/prefer-observers
window.addEventListener('resize', onResize);
onBeforeUnmount(() => {
  if (frame) cancelAnimationFrame(frame);
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('scroll', onScroll, {capture: true});
  window.removeEventListener('resize', onResize);
});
</script>

<template>
  <Teleport :to="container">
    <div
      v-if="target"
      ref="panel"
      class="drcy-cell-resolver"
      role="dialog"
      :aria-labelledby="titleId"
      :style="style"
      @click.stop
      @keydown="onKeydown"
    >
      <div class="drcy-cell-resolver-head">
        <span :id="titleId" class="drcy-cell-resolver-title">{{ target.reference }} · {{ target.header }}</span>
        <span class="drcy-cell-resolver-state" :class="`drcy-cell-resolver-state-${target.flag}`">{{ stateWord }}</span>
        <button type="button" class="drcy-cell-resolver-close" aria-label="Close" @click="emit('close')">×</button>
      </div>
      <p v-if="reason" class="drcy-cell-resolver-reason tw:text-sm">{{ reason }}</p>
      <fieldset v-if="options.length" class="drcy-cell-resolver-options">
        <legend class="tw:text-sm">Pick a value</legend>
        <label v-for="(option, position) in options" :key="position" class="drcy-cell-resolver-option">
          <input type="radio" :name="groupName" :value="option.value" :checked="choice === position" @change="choice = position">
          <span>{{ option.unit ? `${option.value} ${option.unit}` : option.value }}</span>
          <span v-if="option.source" class="drcy-cell-resolver-option-source tw:text-text-light-2 tw:text-xs">{{ option.source }}</span>
        </label>
        <label v-if="target.editable" class="drcy-cell-resolver-option">
          <input type="radio" :name="groupName" value="custom" :checked="choice === CUSTOM" @change="choice = CUSTOM">
          <span>Another value</span>
        </label>
      </fieldset>
      <label v-if="target.editable" class="drcy-cell-resolver-custom tw:text-sm">
        <span>Value</span>
        <input
          ref="valueInput"
          v-model="custom"
          type="text"
          inputmode="decimal"
          :disabled="choice !== CUSTOM"
          :aria-label="`Value for ${target.header}`"
          :aria-describedby="hint ? hintId : undefined"
        >
        <span v-if="unit" class="tw:text-text-light-2">{{ unit }}</span>
      </label>
      <p v-else class="tw:text-text-light-2 tw:text-sm">DRCY computes this cell from the row's inputs; ask it to change those.</p>
      <p v-if="hint" :id="hintId" class="drcy-cell-resolver-hint tw:text-text-light-2 tw:text-sm">{{ hint }}</p>
      <div class="drcy-cell-resolver-actions">
        <button
          v-if="target.editable"
          type="button"
          class="ui tiny primary button"
          :disabled="!canSave"
          :aria-describedby="hint ? hintId : undefined"
          @click="emit('save', value)"
        >
          Save to sheet
        </button>
        <button type="button" class="ui tiny basic button" @click="emit('ask', askText)">Ask DRCY</button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.drcy-cell-resolver {
  position: fixed;
  /* Above the sheet workspace (1100) and the narrow-screen sidebar (1001). */
  z-index: 1200;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: min(22rem, calc(100vw - 1rem));
  /* Many candidates or a short screen scroll inside the panel, so the actions
     stay reachable. */
  max-height: calc(100vh - 1rem);
  padding: 0.75rem;
  overflow-y: auto;
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  box-shadow: 0 6px 18px var(--color-shadow);
}
.drcy-cell-resolver-head {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}
.drcy-cell-resolver-title {
  flex: 1 1 auto;
  font-weight: var(--font-weight-semibold);
  overflow-wrap: anywhere;
}
.drcy-cell-resolver-state {
  padding: 0.05rem 0.4rem;
  font-size: 0.75rem;
  white-space: nowrap;
  border-radius: 1rem;
  box-shadow: inset 0 0 0 1px var(--color-secondary);
}
.drcy-cell-resolver-state-needs_input {
  box-shadow: inset 0 0 0 1px var(--color-yellow);
}
.drcy-cell-resolver-state-refused {
  box-shadow: inset 0 0 0 1px var(--color-red);
}
.drcy-cell-resolver-state-proposed {
  box-shadow: inset 0 0 0 1px var(--color-blue);
}
.drcy-cell-resolver-close {
  padding: 0 0.25rem;
  color: var(--color-text-light-2);
  font: inherit;
  font-size: 1.1rem;
  line-height: 1;
  background: none;
  border: 0;
  cursor: pointer;
}
.drcy-cell-resolver-close:hover {
  color: var(--color-text);
}
.drcy-cell-resolver-reason {
  margin: 0;
  color: var(--color-text-light-1);
  /* A source is often a long URL. */
  overflow-wrap: anywhere;
}
.drcy-cell-resolver-hint {
  margin: 0;
}
.drcy-cell-resolver-options {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin: 0;
  padding: 0;
  border: 0;
}
.drcy-cell-resolver-options > legend {
  padding: 0;
  color: var(--color-text-light-2);
}
.drcy-cell-resolver-option {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0 0.4rem;
  align-items: baseline;
  cursor: pointer;
}
/* A source is often a long URL; it wraps under the value rather than widening
   the panel. */
.drcy-cell-resolver-option-source {
  grid-column: 2;
  min-width: 0;
  overflow-wrap: anywhere;
}
.drcy-cell-resolver-custom {
  display: flex;
  gap: 0.4rem;
  align-items: center;
}
.drcy-cell-resolver-custom > input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0.25rem 0.4rem;
  font: inherit;
  background: var(--color-input-background);
  border: 1px solid var(--color-input-border);
  border-radius: var(--border-radius);
}
.drcy-cell-resolver-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
.drcy-cell-resolver-actions > .button {
  margin: 0 !important;
}
</style>
